import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import type { Metric } from "web-vitals";
import { getNetworkProperties, getRoutePattern } from "./analyticsContext";
import { BaseLogger } from "./baseLogger";

type VitalPayload = {
  name: Metric["name"];
  value: number;
  id: string;
  rating: "good" | "needs-improvement" | "poor";
  page: string;
  device_type: "touch" | "pointer";
  viewport_width: number;
};

const EVENT_NAME = {
  WEB_VITALS: "web-vitals",
  LONG_TASK: "long_task",
  LONG_TASK_SUMMARY: "long_task_summary",
  STORAGE_ESTIMATE: "storage_estimate",
} as const;

/**
 * 모든 성능 메트릭에 공통으로 붙는 프로퍼티.
 *
 * `route_pattern` 은 식별자가 치환된 화면 단위 키다. `page`(= raw pathname)는 값마다
 * 항목이 갈라져 "일일 로그 상세의 LCP" 같은 화면 단위 집계에 쓸 수 없다.
 */
const getCommonProperties = () => ({
  page: window.location.pathname,
  route_pattern: getRoutePattern(),
  ...getNetworkProperties(),
});

/**
 * 입력 방식 기준 디바이스 구분.
 * `innerWidth` 기준은 태블릿·가로모드·분할화면에서 오분류되므로 포인터 정밀도를 쓴다.
 * 화면 크기가 필요한 분석은 함께 보내는 `viewport_width` 로 직접 구간을 나눈다.
 */
const getDeviceType = (): "touch" | "pointer" => (window.matchMedia("(pointer: coarse)").matches ? "touch" : "pointer");

const sendMetric = (eventName: string, properties: Record<string, unknown>) => {
  try {
    mixpanel.track(eventName, { ...getCommonProperties(), ...properties });
  } catch {
    // 분석 오류가 앱 동작에 영향을 주지 않도록 무시
    Sentry.captureException(
      new Error(`Failed to send metric: ${eventName} with properties: ${JSON.stringify(properties)}`),
    );
  }
};

export class PerformanceLogger extends BaseLogger {
  private static _longTaskCount = 0;
  private static _maxLongTask = 0;

  public static logVitals(metric: Metric): void {
    const payload: VitalPayload = {
      name: metric.name,
      value: metric.value,
      id: metric.id,
      rating: metric.rating,
      page: window.location.pathname,
      device_type: getDeviceType(),
      viewport_width: window.innerWidth,
    };

    sendMetric(EVENT_NAME.WEB_VITALS, payload);
  }

  public static initLongTaskObserver() {
    if ("PerformanceObserver" in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === "longtask") {
              PerformanceLogger._longTaskCount += 1;
              PerformanceLogger._maxLongTask = Math.max(PerformanceLogger._maxLongTask, entry.duration);
            }
          }
        });

        observer.observe({ type: "longtask", buffered: true });

        window.addEventListener("pagehide", () => {
          sendMetric(EVENT_NAME.LONG_TASK_SUMMARY, {
            long_task_count: PerformanceLogger._longTaskCount,
            max_long_task_ms: PerformanceLogger._maxLongTask,
          });

          PerformanceLogger._longTaskCount = 0;
          PerformanceLogger._maxLongTask = 0;
        });
      } catch {
        // 브라우저 지원 안 하면 무시
      }
    }
  }

  public static async sampleStorage() {
    if (navigator.storage?.estimate) {
      // usageDetails는 비표준 필드로 브라우저가 지원하는 경우에만 수집
      const estimate = (await navigator.storage.estimate()) as StorageEstimate & {
        usageDetails?: {
          indexedDB: number;
          caches: number;
          serviceWorkerRegistrations: number;
        };
      };

      const { usage, quota, usageDetails } = estimate;

      sendMetric(EVENT_NAME.STORAGE_ESTIMATE, {
        usage: usage ?? 0,
        quota: quota ?? 0,
        ratio: usage && quota ? usage / quota : null,
        // 중첩 객체로 보내면 Mixpanel 에서 breakdown 이 안 되므로 평탄화한다.
        usage_indexed_db: usageDetails?.indexedDB ?? null,
        usage_caches: usageDetails?.caches ?? null,
        usage_service_worker: usageDetails?.serviceWorkerRegistrations ?? null,
      });
    }
  }
}
